import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { LOGO_MARK_URL } from "@/lib/constants";

const AppHandoffContext = createContext(null);
const HANDOFF_KEY = "carbonis:handoff-timestamp";
const RETURN_WINDOW_MS = 30 * 60 * 1000; // 30 minutes

export function useAppHandoff() {
    const ctx = useContext(AppHandoffContext);
    if (!ctx) throw new Error("useAppHandoff must be used inside AppHandoffProvider");
    return ctx;
}

/**
 * Provides:
 * 1. A full-screen "opening Carbonis App" transition when the user clicks the
 *    external nav link (choreographed jump to platform.carbonis.in).
 * 2. A soft "welcome back" toast when the user returns to the marketing site
 *    within RETURN_WINDOW_MS (detected via sessionStorage stamp + tab focus).
 */
export function AppHandoffProvider({ children }) {
    const [visible, setVisible] = useState(false);
    const [returnToast, setReturnToast] = useState(false);

    const openApp = useCallback((url) => {
        try {
            sessionStorage.setItem(HANDOFF_KEY, String(Date.now()));
        } catch (_e) { /* storage may be blocked; overlay still runs */ }
        setVisible(true);
        window.setTimeout(() => {
            window.open(url, "_blank", "noopener,noreferrer");
        }, 350);
        window.setTimeout(() => setVisible(false), 1400);
    }, []);

    // Detect return-from-platform: fire once when the marketing tab regains focus
    // and there is a fresh handoff stamp in sessionStorage.
    useEffect(() => {
        const check = () => {
            try {
                const raw = sessionStorage.getItem(HANDOFF_KEY);
                if (!raw) return;
                const ts = parseInt(raw, 10);
                if (!ts || Date.now() - ts > RETURN_WINDOW_MS) {
                    sessionStorage.removeItem(HANDOFF_KEY);
                    return;
                }
                // Only fire if some real time has passed (avoid firing on the initial click).
                if (Date.now() - ts < 2500) return;
                sessionStorage.removeItem(HANDOFF_KEY);
                setReturnToast(true);
                window.setTimeout(() => setReturnToast(false), 6500);
            } catch (_e) { /* noop */ }
        };
        const onVisible = () => { if (!document.hidden) check(); };
        document.addEventListener("visibilitychange", onVisible);
        window.addEventListener("focus", check);
        // Also check once on mount (covers case where user hits the browser back button).
        check();
        return () => {
            document.removeEventListener("visibilitychange", onVisible);
            window.removeEventListener("focus", check);
        };
    }, []);

    return (
        <AppHandoffContext.Provider value={{ openApp }}>
            {children}

            {/* Outbound handoff overlay */}
            <AnimatePresence>
                {visible && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="fixed inset-0 z-[100] bg-paper/95 backdrop-blur-md flex items-center justify-center"
                        data-testid="app-handoff-overlay"
                    >
                        <div className="flex flex-col items-center gap-8 max-w-md px-8 text-center">
                            <motion.img
                                src={LOGO_MARK_URL}
                                alt="Carbonis"
                                initial={{ scale: 0.85, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                                className="w-16 h-16"
                            />
                            <div className="flex flex-col gap-3">
                                <div className="font-mono text-[11px] tracking-[0.28em] uppercase text-gold">
                                    Opening Carbonis App
                                </div>
                                <div className="font-serif text-2xl lg:text-3xl text-navy leading-tight tracking-[-0.01em]">
                                    Handing you over to <em className="text-gold not-italic font-light">platform.carbonis.in</em>
                                </div>
                            </div>
                            <div className="w-24 h-px bg-navy/15 overflow-hidden">
                                <motion.div
                                    initial={{ x: "-100%" }}
                                    animate={{ x: "100%" }}
                                    transition={{ duration: 1.1, ease: "linear" }}
                                    className="w-full h-full bg-gold"
                                />
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Inbound welcome-back toast */}
            <AnimatePresence>
                {returnToast && (
                    <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 12, scale: 0.98 }}
                        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                        className="fixed bottom-8 right-8 z-[90] max-w-sm bg-paper border border-navy/15 shadow-[0_10px_40px_-12px_rgba(11,31,42,0.25)] px-6 py-5 pr-8"
                        data-testid="handoff-return-toast"
                        onClick={() => setReturnToast(false)}
                        role="status"
                    >
                        <div className="flex items-start gap-4">
                            <img src={LOGO_MARK_URL} alt="" className="w-9 h-9 mt-0.5" />
                            <div className="flex flex-col gap-1.5">
                                <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.24em] uppercase text-gold">
                                    <ArrowLeft size={11} strokeWidth={1.75} />
                                    Welcome back
                                </div>
                                <div className="font-serif text-lg leading-snug text-navy">
                                    You're back from the <em className="text-gold not-italic font-light">Carbonis App.</em>
                                </div>
                                <div className="text-navy/55 text-[13px] leading-relaxed">
                                    Ready to keep exploring the case for climate as financial risk?
                                </div>
                            </div>
                        </div>
                        <button
                            aria-label="Dismiss"
                            onClick={(e) => { e.stopPropagation(); setReturnToast(false); }}
                            className="absolute top-2 right-2 text-navy/40 hover:text-navy text-lg leading-none font-serif"
                            data-testid="handoff-return-toast-dismiss"
                        >
                            ×
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>
        </AppHandoffContext.Provider>
    );
}
